import { ConsentManager, ConsentManagerVendor, ConsentRequirement, ConsentState } from '../consent.types';
import { deepEqual, injectScriptOnce } from '../utils/consentHelper';

export type ConsentmanagerDeConfig = {
    /** Code-ID (recommended). Example: 'de8b8d81224a' */
    codeId: string;
    /** Optional legacy CMP-ID support (numeric). Not needed if codeId is set. */
    cmpId?: number | string;
    /** Delivery host & CDN (take from CM "Get Codes" page if customized). */
    host?: string; // default: 'delivery.consentmanager.net'
    cdn?: string; // default: 'cdn.consentmanager.net'
    /** Optional flags mapped to window variables used by the loader. */
    gdprAppliesGlobally?: boolean;
    params?: string;
    /**
     * Optional mappings to allow friendly names in hasConsent({ services/categories })
     * Example: vendorMap: { 'GoogleAnalytics4': 's26' }, purposeMap: { marketing: 's3' }
     */
    vendorMap?: Record<string, string>;
    purposeMap?: Record<string, string | number>;
    /** Banner open behavior */
    openAs?: 'welcome' | 'preferences';
};

export class ConsentmanagerDeAdapter implements ConsentManager {
    public name: ConsentManagerVendor = 'consentmanagerde';
    private ready = false;
    private state: ConsentState = { services: {}, categories: {} };
    private unsubscribers: Array<() => void> = [];

    async load(cfg: ConsentmanagerDeConfig): Promise<void> {
        if (!cfg?.codeId && !cfg?.cmpId) throw new Error('consentmanagerde: codeId (preferred) or cmpId required');

        // Set global variables BEFORE loading the script (per CM docs)
        window.gdprAppliesGlobally = cfg.gdprAppliesGlobally ?? window.gdprAppliesGlobally ?? false;
        if (cfg.codeId) window.cmp_cdid = cfg.codeId; // preferred
        if (cfg.cmpId) window.cmp_id = cfg.cmpId as any; // legacy fallback
        window.cmp_host = cfg.host ?? window.cmp_host ?? 'delivery.consentmanager.net';
        window.cmp_cdn = cfg.cdn ?? window.cmp_cdn ?? 'cdn.consentmanager.net';
        if (cfg.params) window.cmp_params = cfg.params;

        // Load the CMP core
        await injectScriptOnce({
            id: 'consentmanager-cmp',
            src: `https://${window.cmp_cdn}/delivery/cmp.min.js`,
            before: 'head-prepend',
        });

        // Mark ready after settings/consent event or first computed state
        await new Promise<void>((resolve) => {
            const markReady = () => {
                if (!this.ready) {
                    this.ready = true;
                    resolve();
                }
            };

            // Try event-based readiness first
            const settingsHandler = () => {
                this.refreshState();
                markReady();
            };
            const consentHandler = () => {
                this.refreshState();
                markReady();
            };

            if (typeof window.__cmp === 'function') {
                try {
                    window.__cmp('addEventListener', ['settings', settingsHandler, false], null);
                    window.__cmp('addEventListener', ['consent', consentHandler, false], null);
                    this.unsubscribers.push(() =>
                        window.__cmp('removeEventListener', ['settings', settingsHandler, false], null),
                    );
                    this.unsubscribers.push(() =>
                        window.__cmp('removeEventListener', ['consent', consentHandler, false], null),
                    );
                } catch {
                    /* no-op */
                }
            }

            // Fallback: poll until we can read a state
            const start = Date.now();
            const poll = () => {
                this.refreshState();
                if (Object.keys(this.state.services).length || Date.now() - start > 2500) markReady();
                else setTimeout(poll, 120);
            };
            poll();
        });
    }

    isReady(): boolean {
        return this.ready;
    }
    getState(): ConsentState {
        return this.state;
    }

    onChange(cb: (state: ConsentState) => void): () => void {
        const handler = () => {
            const before = this.state;
            this.refreshState();
            if (!deepEqual(before, this.state)) cb(this.state);
        };

        if (typeof window.__cmp === 'function') {
            try {
                window.__cmp('addEventListener', ['consent', handler, false], null);
                window.__cmp('addEventListener', ['settings', handler, false], null);
                this.unsubscribers.push(() => window.__cmp('removeEventListener', ['consent', handler, false], null));
                this.unsubscribers.push(() => window.__cmp('removeEventListener', ['settings', handler, false], null));
            } catch {
                /* ignore */
            }
        }

        // Light polling as safety net (some setups don’t dispatch events reliably)
        let stopped = false;
        const poll = () => {
            if (!stopped) {
                handler();
                setTimeout(poll, 800);
            }
        };
        poll();

        return () => {
            stopped = true;
            this.unsubscribers.forEach((u) => u());
        };
    }

    hasConsent(req: ConsentRequirement): boolean {
        const state = this.state;

        // Resolve service (vendor) IDs via optional mapping
        const resolveVendor = (k: string) => {
            const id = this.configVendorMap?.[k] ?? k;
            return state.services[id] ?? state.services[k];
        };

        const okServices = (req.services ?? []).every((s) => resolveVendor(s) === true);

        const resolvePurpose = (k: string) => {
            const id = String(this.configPurposeMap?.[k] ?? k);
            return state.categories[id] ?? state.categories[k] ?? state.categories[id];
        };

        const okCategories = (req.categories ?? []).every((c) => resolvePurpose(c) === true);

        return okServices && okCategories;
    }

    showBanner(): void {
        try {
            if (typeof window.__cmp === 'function') {
                if (this.openAs === 'preferences') window.__cmp('showScreenAdvanced');
                else window.__cmp('showScreen');
            }
        } catch {
            /* ignore */
        }
    }

    // --- internals ---
    private configVendorMap?: Record<string, string>;
    private configPurposeMap?: Record<string, string | number>;
    private openAs: 'welcome' | 'preferences' = 'welcome';

    private refreshState() {
        try {
            const data = typeof window.__cmp === 'function' ? window.__cmp('getCMPData') : null;
            if (!data || typeof data !== 'object') return;

            const services: Record<string, boolean> = {};
            const categories: Record<string, boolean> = {};

            // Vendors (services)
            const v = (data.vendorConsents ?? {}) as Record<string, any>;
            for (const [vendorId, allowed] of Object.entries(v)) {
                services[vendorId] = !!allowed;
            }

            // Purposes (categories)
            const p = (data.purposeConsents ?? {}) as Record<string, any>;
            for (const [purposeId, allowed] of Object.entries(p)) {
                categories[String(purposeId)] = !!allowed;
            }

            this.state = { services, categories };
        } catch {
            /* ignore */
        }
    }
}
